import React from 'react';
import { Link } from 'react-router-dom';
import { FacebookIcon, TwitterIcon, InstagramIcon, YouTubeIcon } from '../../icons/SocialIcons';
import { ExternalLinkIcon, Phone, Mail, MapPin } from '../../icons';
import FooterLogoCarousel from '../sections/FooterLogoCarousel';
import { useData } from '../../context/DataContext';

const Footer: React.FC = () => {
    const { socialLinks, governmentPortals, lastUpdated, generalInfo } = useData();

    // Use lastUpdated from context (fetch time) or fallback to current date
    const dateObj = lastUpdated || new Date();
    const lastUpdatedDate = dateObj.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });

    const quickLinks = [
        { label: "Home", path: "/" },
        { label: "About Us", path: "/about" },
        { label: "Services", path: "/services" },
        { label: "Contact Us", path: "/location" },
        { label: "Site Map", path: "/sitemap" },
        { label: "Archives", path: "/news" },
    ];

    const policyLinks = [
        { label: "Website Policies", path: "/terms" },
        { label: "Privacy Policy", path: "/privacy" },
        { label: "Hyperlink Policy", path: "/hyperlink-policy" },
        { label: "Copyright Policy", path: "/copyright-policy" },
        { label: "Accessibility Statement", path: "/accessibility" },
        { label: "Help", path: "/help" },
        { label: "Feedback", path: "/feedback" },
    ];

    const governmentLinks = governmentPortals?.length > 0 ? governmentPortals.map((item: any) => ({
        label: item.name,
        url: item.url,
        description: item.name
    })) : [
        { label: "National Portal of India", url: "https://india.gov.in", description: "india.gov.in" },
        { label: "Government of Haryana", url: "https://haryana.gov.in", description: "haryana.gov.in" },
        { label: "ULB Haryana", url: "https://ulbharyana.gov.in", description: "ulbharyana.gov.in" },
        { label: "MyGov India", url: "https://mygov.in", description: "mygov.in" },
        { label: "Digital India", url: "https://digitalindia.gov.in", description: "digitalindia.gov.in" },
    ];

    const ExternalLinkButton: React.FC<{ href: string; children: React.ReactNode; primary?: boolean }> = ({
        href,
        children,
        primary = false
    }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${primary
                ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg hover:shadow-xl'
                : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
            title={`Visit ${children} (opens in new window)`}
        >
            {children}
            <ExternalLinkIcon className="w-4 h-4" aria-hidden="true" />
            <span className="sr-only">(opens in new window)</span>
        </a>
    );

    return (
        <footer className="relative z-10 mt-20" role="contentinfo">
            <FooterLogoCarousel />
            <div className="bg-gradient-to-b from-[#1a365d] to-[#0f2744] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
                        <div className="lg:col-span-1">
                            {(generalInfo?.organization_name || socialLinks?.youtube_handle) && (
                                <h3 className="text-lg font-bold text-white mb-4">
                                    {generalInfo?.organization_name || socialLinks?.youtube_handle}
                                </h3>
                            )}
                            {(generalInfo?.footer_desc || socialLinks?.footer_desc) && (
                                <p className="text-sm text-gray-300 mb-6 leading-relaxed">
                                    {generalInfo?.footer_desc || socialLinks?.footer_desc}
                                </p>
                            )}

                            <div className="space-y-3 text-sm">
                                {(generalInfo?.office_address || socialLinks?.office_address) && (
                                    <div className="flex items-start gap-2 text-gray-300">
                                        <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-blue-300" />
                                        <span>{generalInfo?.office_address || socialLinks?.office_address}</span>
                                    </div>
                                )}
                                {(generalInfo?.office_phone || socialLinks?.phone_primary) && (
                                    <div className="flex items-center gap-2 text-gray-300">
                                        <Phone className="w-4 h-4 shrink-0 text-blue-300" />
                                        <span>{generalInfo?.office_phone || socialLinks?.phone_primary}</span>
                                    </div>
                                )}
                                {(generalInfo?.office_email || socialLinks?.email_primary) && (
                                    <div className="flex items-center gap-2 text-gray-300">
                                        <Mail className="w-4 h-4 shrink-0 text-blue-300" />
                                        <a href={`mailto:${generalInfo?.office_email || socialLinks?.email_primary}`} className="hover:text-white transition-colors">
                                            {generalInfo?.office_email || socialLinks?.email_primary}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-300 mb-4">
                                Quick Links
                            </h3>
                            <nav aria-label="Quick Links">
                                <ul className="space-y-2">
                                    {quickLinks.map((link, idx) => (
                                        <li key={idx}>
                                            <Link
                                                to={link.path}
                                                className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
                                            >
                                                <span className="text-blue-400" aria-hidden="true">›</span>
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-300 mb-4">
                                Policies
                            </h3>
                            <nav aria-label="Policy Links">
                                <ul className="space-y-2">
                                    {policyLinks.map((link, idx) => (
                                        <li key={idx}>
                                            <Link
                                                to={link.path}
                                                className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
                                            >
                                                <span className="text-blue-400" aria-hidden="true">›</span>
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold uppercase tracking-wider text-blue-300 mb-4">
                                Government Links
                            </h3>
                            <nav aria-label="External Government Links">
                                <ul className="space-y-2">
                                    {governmentLinks.map((link, idx) => (
                                        <li key={idx}>
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors group"
                                                title={`Visit ${link.label} (opens in new window)`}
                                            >
                                                <span className="text-blue-400" aria-hidden="true">›</span>
                                                {link.description}
                                                <ExternalLinkIcon className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                                                <span className="sr-only">(opens in new window)</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className="mt-6">
                                <ExternalLinkButton href="https://india.gov.in" primary>
                                    National Portal
                                </ExternalLinkButton>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/10">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="flex items-center gap-4">
                                <span className="text-sm text-gray-400">Follow Us:</span>
                                <div className="flex gap-3">
                                    {socialLinks?.twitter && (
                                        <a
                                            href={socialLinks.twitter}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                            title="Follow us on Twitter/X (opens in new window)"
                                            aria-label="Twitter/X"
                                        >
                                            <TwitterIcon className="w-4 h-4 fill-current" />
                                        </a>
                                    )}
                                    {socialLinks?.youtube && (
                                        <a
                                            href={socialLinks.youtube}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                            title="Subscribe on YouTube (opens in new window)"
                                            aria-label="YouTube"
                                        >
                                            <YouTubeIcon className="w-4 h-4 fill-current" />
                                        </a>
                                    )}
                                    {socialLinks?.facebook && (
                                        <a
                                            href={socialLinks.facebook}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                            title="Like us on Facebook (opens in new window)"
                                            aria-label="Facebook"
                                        >
                                            <FacebookIcon className="w-4 h-4 fill-current" />
                                        </a>
                                    )}
                                    {socialLinks?.instagram && (
                                        <a
                                            href={socialLinks.instagram}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                            title="Follow us on Instagram (opens in new window)"
                                            aria-label="Instagram"
                                        >
                                            <InstagramIcon className="w-4 h-4 fill-current" />
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="text-center md:text-right">
                                <p className="text-sm text-gray-300">
                                    Website last updated on: <strong className="text-white">{lastUpdatedDate}</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-[#0a1929] border-t border-white/5">
                    <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-400">
                            <p>
                                © {new Date().getFullYear()} Municipal Council Jhajjar, Government of Haryana. All rights reserved.
                            </p>
                            <div className="flex items-center gap-4">
                                <span>Designed & Developed by IT-Cell, MC Jhajjar</span>
                                <span className="hidden md:inline" aria-hidden="true">|</span>
                                <span className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" aria-hidden="true"></span>
                                    <span>Site Active</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;

